// components/Education.js
import { Button, Label, Modal, TextInput, Datepicker, Select, Textarea, Checkbox, Carousel } from 'flowbite-react';
import { useState } from 'react';
import useEducationController from '../controllers/useEducationController';
import { useSession } from '../context/SessionContext';

function Education() {
  const { session } = useSession();
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEducation, setCurrentEducation] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [isCurrent, setIsCurrent] = useState(false);
  const [formData, setFormData] = useState({
    institution: '',
    type: '',
    start: '',
    end: '',
    degree: '',
    field: '',
    gpa: '',
    achievements: '',
  });
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState('');

  const {
    educations,
    loading,
    error,
    addEducation,
    updateEducation,
    deleteEducation,
    deleteImage,
  } = useEducationController(session);

  // Sort educations by start date
  const sortedEducations = [...educations].sort((a, b) => 
    new Date(b.start) - new Date(a.start)
  );

  const onCloseModal = () => {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentEducation(null);
    setImages([]);
    setImageError('');
    setIsCurrent(false);
    setFormData({
      institution: '',
      type: '',
      start: '',
      end: '',
      degree: '',
      field: '',
      gpa: '',
      achievements: '',
    });
    setStartDate(new Date());
    setEndDate(new Date());
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const validFiles = [];
      for (const file of files) {
        if (file.size > 500 * 1024) {
          setImageError('Each image must be less than 500KB');
          return;
        }
        validFiles.push(file);
      }
      setImages((prevImages) => [...prevImages, ...validFiles]);
      setImageError('');
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (image, index) => {
    try {
      if (isEditMode && image.path) {
        await deleteImage(image.path);
        setCurrentEducation(prev => ({
          ...prev,
          images: prev.images.filter(img => img.path !== image.path)
        }));
      }
      setImages(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.institution ||
      !formData.type ||
      !startDate ||
      !formData.field
    ) {
      alert('Please fill in all required fields: Institution, Type, Start Date, and Field of Study.');
      return;
    }

    if (images.length > 0 && imageError) {
      alert('Please fix image errors before submitting');
      return;
    }

    try {
      const educationData = {
        ...formData,
        start: startDate.toISOString(),
        end: isCurrent ? null : endDate.toISOString(),
        degree: formData.degree || null,
        gpa: formData.gpa || null,
        achievements: formData.achievements || null,
      };

      if (isEditMode && currentEducation) {
        const newImageFiles = images.filter(img => img instanceof File);
        await updateEducation(currentEducation.id, educationData, newImageFiles);
      } else {
        await addEducation(educationData, images);
      }

      onCloseModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleEdit = (education) => {
    setCurrentEducation(education);
    setIsEditMode(true);
    setFormData({
      institution: education.institution,
      type: education.type,
      start: education.start,
      end: education.end,
      degree: education.degree || '',
      field: education.field,
      gpa: education.gpa || '',
      achievements: education.achievements || '',
    });
    setStartDate(new Date(education.start));
    setEndDate(education.end ? new Date(education.end) : new Date());
    setIsCurrent(!education.end);
    setImages(education.images || []);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this education?')) {
      await deleteEducation(id);
    }
  };

  return (
    <div className="flex">
      <section className="flex-1 py-5 md:px-5 bg-gray-100 dark:bg-gray-900">
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className="my-6 md:col-span-2 md:pl-6">
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl 2xl:text-8xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Education
              </span>
            </h1>
            <p className="max-w-3xl md:mt-6 mt-2 mr-4 md:text-lg text-sm text-left text-gray-700 dark:text-white">
              Add your education history to showcase your academic background and achievements.
            </p>
          </div>
          <div className="mx-auto my-auto md:col-span-1">
            <Button color="blue" onClick={() => setOpenModal(true)}>
              <b>+</b> Add Education
            </Button>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="mt-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800">
              Error: {error}
            </div>
          ) : (
            <div className="relative md:pl-8 md:ml-4 pl-2 ml-2">
              {/* Vertical line */}
              <div 
                className="absolute left-0 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                style={{ marginLeft: '7px' }}
              ></div>
              
              {sortedEducations.map((edu, index) => (
                <div 
                  key={edu.id} 
                  className="relative mb-8 md:pl-6 pl-4 group"
                >
                  {/* Timeline dot */}
                  <div 
                    className="absolute w-4 h-4 bg-teal-600 rounded-full -left-2 top-5 border-4 border-white dark:border-gray-900"
                    style={{ left: '4px' }}
                  ></div>
                  
                  {/* Education card */}
                  <div className="bg-white rounded-lg shadow-lg dark:bg-gray-800 md:p-6 p-3 transition-all duration-300 group-hover:shadow-xl group-hover:translate-x-2">
                    <div className="flex flex-col md:flex-row">
                      {edu.images && edu.images.length > 0 && (
                        <div className="md:w-2/5 lg:w-1/3 h-64 md:h-auto mb-4 md:mb-0">
                          <Carousel>
                            {edu.images.map((image, idx) => (
                              <div key={idx} className="relative">
                                <img
                                  src={image.publicUrl}
                                  alt={`Education ${idx + 1}`}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                                <Button
                                  color="red"
                                  size="xs"
                                  className="absolute top-2 right-2 z-10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveImage(image, idx);
                                  }}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </Carousel>
                        </div>
                      )}
                      
                      <div className={`flex-1 ${edu.images ? 'md:ml-6' : ''}`}>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                          <h2 className="md:text-xl font-bold text-gray-800 dark:text-white">
                            {edu.institution}
                          </h2>
                          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1 md:mt-0">
                            {new Date(edu.start).toLocaleDateString('en-GB', {
                              month: 'short',
                              year: 'numeric'
                            })} - {edu.end ? 
                              new Date(edu.end).toLocaleDateString('en-GB', {
                                month: 'short',
                                year: 'numeric'
                              }) : 'Present'}
                          </span>
                        </div>
                        
                        <div className="flex items-center mb-4 text-gray-600 dark:text-gray-300">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 14l9-5-9-5-9 5 9 5z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                            />
                          </svg>
                          <span>{edu.degree || 'No degree specified'}</span>
                        </div>

                        <div className="mb-4">
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Field of Study:
                          </span>
                          <p className="text-gray-600 dark:text-gray-400">{edu.field}</p>
                        </div>

                        {edu.gpa && (
                          <div className="mb-4">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              GPA:
                            </span>
                            <p className="text-gray-600 dark:text-gray-400">{edu.gpa}</p>
                          </div>
                        )}

                        {edu.achievements && (
                          <div className="bg-gray-50 dark:bg-gray-700 md:p-4 p-2 rounded-lg mb-4">
                            <h3 className="text-sm font-semibold mb-2 text-gray-800 dark:text-white">
                              Key Achievements:
                            </h3>
                            <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                              {edu.achievements.split(";").map((item, idx) => (
                                <li key={idx}>{item.trim()}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button 
                            color="red" 
                            onClick={(e) => { e.stopPropagation(); handleDelete(edu.id); }}
                            size="sm"
                          >
                            Delete
                          </Button>
                          <Button 
                            color="blue" 
                            onClick={(e) => { e.stopPropagation(); handleEdit(edu); }}
                            size="sm"
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal for Adding/Editing Education */}
        <Modal show={openModal} onClose={onCloseModal} popup className="backdrop-blur-sm bg-white/30">
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                {isEditMode ? 'Edit Education' : 'Add Education'}
              </h3>
              <div>
                <Label htmlFor="institution" value="Institution Name *" />
                <TextInput 
                  id="institution" 
                  placeholder="e.g. Harvard University" 
                  required 
                  value={formData.institution} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="type" value="Type of Education *" />
                <Select 
                  id="type" 
                  required 
                  value={formData.type} 
                  onChange={handleInputChange}
                >
                  <option>Choose Education Type</option>
                  <option>University</option>
                  <option>High School</option>
                  <option>Training</option>
                  <option>Others</option>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start" value="Start Date *" />
                  <Datepicker
                    id="start"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end" value="End Date" />
                  <Datepicker
                    id="end"
                    selected={isCurrent ? null : endDate}
                    onChange={(date) => setEndDate(date)}
                    disabled={isCurrent}
                  />
                  <div className="mt-2">
                    <Checkbox
                      id="isCurrent"
                      checked={isCurrent}
                      onChange={(e) => setIsCurrent(e.target.checked)}
                    />
                    <Label htmlFor="isCurrent" className="ml-2">
                      I currently study here
                    </Label>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="field" value="Field of Study *" />
                <TextInput 
                  id="field" 
                  placeholder="e.g. Computer Science" 
                  required 
                  value={formData.field} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="degree" value="Degree" />
                <TextInput 
                  id="degree" 
                  placeholder="e.g. Bachelor of Science" 
                  value={formData.degree} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="gpa" value="GPA" />
                <TextInput 
                  id="gpa" 
                  placeholder="e.g. 3.8" 
                  type="number" 
                  step="0.01" 
                  min="0" 
                  max="4" 
                  value={formData.gpa} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="achievements" value="Key Achievements (separate with ;)" />
                <Textarea 
                  id="achievements" 
                  placeholder="Example: Dean's List; Best Thesis Award" 
                  value={formData.achievements} 
                  onChange={handleInputChange} 
                />
              </div>
              <div>
                <Label htmlFor="images" value="Image Documentation (Max 500KB per image)" />
                <input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                />
                {imageError && <p className="mt-2 text-sm text-red-600 dark:text-red-500">{imageError}</p>}
                <div className="mt-4 space-y-2">
                  {images?.map((image, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        {image.publicUrl && (
                          <img src={image.publicUrl} alt={`Preview ${index + 1}`} className="w-12 h-12 object-cover rounded mr-3" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                          {image.path ? image.path.split('/').pop() : image.name}
                        </span>
                      </div>
                      <Button 
                        color="red" 
                        size="xs" 
                        onClick={() => handleRemoveImage(image, index)}
                        className="ml-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full">
                <Button color="blue" type="submit" className="w-full">
                  {isEditMode ? 'Update Education' : 'Add Education'}
                </Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Education;